import React, { MouseEvent, FormEvent, ChangeEvent, useState } from "react";
import "./SaveOfferForm.css";
import Text from "src/components/Input/Text/Text";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import Checkbox from "src/components/Input/Checkbox/Checkbox";
import TextArea from "src/components/Input/TextArea/TextArea";
import { usePopup } from "src/contexts/PopupContext";
import { useLoader } from "src/contexts/LoaderContext";
import { useView } from "src/contexts/ViewContext";

const nlPostcodeRegex = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
const bePostcodeRegex = /^\d{4}$/;
const phoneRegex = /^\+?\d{10}$/;

const SaveOfferForm: React.FC = () => {
  const { request } = useConfiguration();
  const {
    isSaveOfferFormVisible,
    setIsSaveOfferFormVisible,
    setIsResultPopupVisible,
    setResultPopupText,
    setResultPopupImage,
    saveOfferMode,
  } = usePopup();
  const {
    setIsWhiteSceneLoaderVisible,
    setIsMainLoaderVisible,
    setMainLoaderText,
  } = useLoader();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street: "",
    house_number: "",
    city: "",
    postcode: "",
    country: "Nederland",
    delivery_option: "Afhalen: GRATIS",
    comment: "",
  });

  const [errors, setErrors] = useState<Partial<typeof formData>>({});
  const [isCommentVisible, setIsCommentVisible] = useState(false);
  const [openedSelects, setOpenedSelects] = useState<
    Partial<Record<keyof typeof formData, boolean>>
  >({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsCommentVisible(checked);
  };

  const setSelectOpenState = (name: keyof typeof formData, isOpen: boolean) => {
    setOpenedSelects((prev) => ({
      ...prev,
      [name]: isOpen,
    }));
  };

  const toggleSelectOpenState = (name: keyof typeof formData) => {
    setOpenedSelects((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleSelectChange = (
    name: keyof typeof formData,
    e: ChangeEvent<HTMLSelectElement>,
  ) => {
    handleChange(e);
    setSelectOpenState(name, false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<typeof formData> = {};

    for (const key in formData) {
      if (key !== "comment" && !formData[key as keyof typeof formData]) {
        newErrors[key as keyof typeof formData] = "Dit veld is verplicht.";
      }
    }

    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Veldwaarde moet toegestane invoer te vullen.";
    }

    if (formData.postcode) {
      if (
        formData.country === "Nederland" &&
        !nlPostcodeRegex.test(formData.postcode)
      ) {
        newErrors.postcode =
          'Postcode moet 4 cijfers en 2 letters zijn. (bijv. "1234 AB").';
      } else if (
        formData.country === "België" &&
        !bePostcodeRegex.test(formData.postcode)
      ) {
        newErrors.postcode = 'Postcode moet 4 cijfers zijn. (bijv. "1234").';
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const formObject: Record<string, any> = {};

    setIsSaveOfferFormVisible(false);
    setIsWhiteSceneLoaderVisible(true);
    setMainLoaderText("Werk in uitvoering. We slaan nu uw overkapping op.");
    setIsMainLoaderVisible(true);

    try {
      formData.forEach((value, key) => {
        formObject[key] = String(value);
      });
      formObject["mode"] = saveOfferMode;
      const response = await request.addOffer(formObject);
      setIsWhiteSceneLoaderVisible(false);
      setIsMainLoaderVisible(false);
      setResultPopupText(response.data.message);
      setResultPopupImage(response.data.image || "");
      setIsResultPopupVisible(true);
    } catch (error) {
      console.error("Error:", error);
      setIsSaveOfferFormVisible(false);

      setTimeout(() => {
        setIsWhiteSceneLoaderVisible(false);
        setIsMainLoaderVisible(false);
        setResultPopupText("Er is iets misgegaan. Probeer het later opnieuw.");
        setIsResultPopupVisible(true);
      }, 1000);
    }
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    setIsSaveOfferFormVisible(false);
  };

  const renderField = (name: keyof typeof formData, label: string) => (
    <div className={`save-offer-form-field field-${name}`}>
      <Text
        name={name}
        label={label}
        content={formData[name]}
        onChange={handleChange}
      />
      {errors[name] && <div className="error-msg">{errors[name]}</div>}
    </div>
  );

  const renderSelectField = (
    name: keyof typeof formData,
    label: string,
    options: { value: string; label: string }[],
  ) => (
    <div className={`save-offer-form-field field-${name}`}>
      <div className="text-input">
        <label htmlFor={name}>{label}</label>
        <div
          className={`save-offer-form-select ${openedSelects[name] ? "open" : ""}`}
        >
          <select
            id={name}
            name={name}
            value={formData[name]}
            onChange={(e) => handleSelectChange(name, e)}
            onMouseDown={() => toggleSelectOpenState(name)}
            onTouchStart={() => toggleSelectOpenState(name)}
            onBlur={() => setSelectOpenState(name, false)}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter" || e.key === "F4") {
                setSelectOpenState(name, true);
                return;
              }

              if (e.altKey && e.key === "ArrowDown") {
                setSelectOpenState(name, true);
                return;
              }

              if (e.altKey && e.key === "ArrowUp") {
                setSelectOpenState(name, false);
                return;
              }

              if (e.key === "Escape" || e.key === "Tab") {
                setSelectOpenState(name, false);
              }
            }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M16 12L10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
      {errors[name] && <div className="error-msg">{errors[name]}</div>}
    </div>
  );

  const renderTextAreaField = (
    name: keyof typeof formData,
    placeholder: string,
  ) => (
    <div
      className={`save-offer-form-field save-offer-form-textarea-field field-${name}`}
    >
      <TextArea
        name={name}
        placeholder={placeholder}
        content={formData[name]}
        onChange={handleChange}
      />
      {errors[name] && <div className="error-msg">{errors[name]}</div>}
    </div>
  );

  const dataFormHeader = {
    imageUrl: "../images/logo.svg",
    descriptionHeader: "Mijn overkapping opslaan + offerte opstellen",
    textDescription:
      "Vul je gegevens in en wij sturen je per email de samengestelde overkapping toe en nemen contact met je op om de offerte door te spreken.",
  };

  if (saveOfferMode === "purchase") {
    dataFormHeader.descriptionHeader = "Mijn overkapping bestellen";
    dataFormHeader.textDescription =
      "Vul je gegevens in en wij sturen je per email de bestelbevestiging van de samengestelde overkapping toe en nemen contact met je op om de voortgang van de opdracht door te spreken.";
  }

  const countryOptions = [
    { value: "Nederland", label: "Nederland" },
    { value: "België", label: "België" },
    { value: "Overige", label: "Overige" },
  ];
  const deliveryOptions = [
    { value: "Afhalen: GRATIS", label: "Afhalen: GRATIS" },
    { value: "Bezorgen: € 175,-", label: "Bezorgen: € 175,-" },
  ];

  return (
    <>
      {isSaveOfferFormVisible && (
        <div className="save-offer-form" onClick={handleOverlayClick}>
          <div className="save-offer-form-overlay">
            <div
              className="close-form"
              onClick={() => setIsSaveOfferFormVisible(false)}
            >
              ×
            </div>
            <div className="save-offer-form-header">
              <img
                src={dataFormHeader.imageUrl}
                alt="buitenlevenxxl"
                // width="200"
                height="50"
              />
            </div>
            <div className="save-offer-form-overlay-text">
              <h2>{dataFormHeader.descriptionHeader}</h2>
              <p>{dataFormHeader.textDescription}</p>
            </div>
            <form id="saveOfferForm" method="post" onSubmit={handleSubmit}>
              {renderField("first_name", "Voornaam")}
              {renderField("last_name", "Achternaam")}
              {renderField("email", "E-mailadres")}
              {renderField("phone", "Telefoonnummer")}
              {renderField("street", "Straatnaam")}
              {renderField("house_number", "Huisnr")}
              {renderField("postcode", "Postcode")}
              {renderField("city", "Plaatsnaam")}
              {renderSelectField("country", "Land", countryOptions)}
              {saveOfferMode === "purchase" &&
                renderSelectField(
                  "delivery_option",
                  "Bezorgoptie",
                  deliveryOptions,
                )}
              {saveOfferMode === "offer" && (
                <div className="save-offer-form-comment not-required">
                  <Checkbox
                    checked={isCommentVisible}
                    onChange={handleCheckboxChange}
                    label="Ik wil nog wat veranderen aan de overkapping"
                  />
                </div>
              )}
              {saveOfferMode === "offer" &&
                isCommentVisible &&
                renderTextAreaField(
                  "comment",
                  "Heeft u nog een andere aanmerking of wilt u graag iets anders zien in de overkapping vermeld het dan hier",
                )}
              <button type="submit">
                {saveOfferMode === "offer" ? "Opslaan" : "Bestellen"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SaveOfferForm;
